import { c as _c } from "react/compiler-runtime";
import { Facebook, Github, Globe, Instagram, Linkedin, Twitch, Twitter, Youtube, type LucideProps } from "lucide-react";
import type { ComponentType } from "react";
type SocialIconComponent = ComponentType<LucideProps>;

/**
 * Lucide doesn't ship a TikTok glyph; keep a filled brand mark that matches
 * the 24×24 Lucide viewBox and uses `currentColor`.
 */
function TikTokIcon(t0) {
  const $ = _c(9);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      size: t1,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const size = t1 === undefined ? 24 : t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.77a8.2 8.2 0 0 0 4.76 1.52V6.84a4.85 4.85 0 0 1-1-.15z" />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== className || $[6] !== props || $[7] !== size) {
    t3 = <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true" {...props}>{t2}</svg>;
    $[5] = className;
    $[6] = props;
    $[7] = size;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
const SOCIAL_ICONS: Record<string, SocialIconComponent> = {
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  linkedin: Linkedin,
  twitter: Twitter,
  x: Twitter,
  tiktok: TikTokIcon,
  twitch: Twitch,
  github: Github
};
export type SocialNetworkIconProps = LucideProps & {
  /** CMS social link key, e.g. `instagram`, `tiktok`, `linkedin`. */
  network: string;
};

/**
 * Renders the Lucide (or TikTok) icon for a social network key.
 * Unknown keys fall back to Globe.
 */
export function SocialNetworkIcon(t0) {
  const $ = _c(10);
  let network;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      network,
      size: t1,
      strokeWidth: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = network;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    network = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const size = t1 === undefined ? 24 : t1;
  const strokeWidth = t2 === undefined ? 2 : t2;
  const normalized = network.trim().toLowerCase();
  const Icon = SOCIAL_ICONS[normalized] ?? Globe;
  let t3;
  if ($[5] !== Icon || $[6] !== props || $[7] !== size || $[8] !== strokeWidth) {
    t3 = <Icon size={size} strokeWidth={strokeWidth} aria-hidden={true} {...props} />;
    $[5] = Icon;
    $[6] = props;
    $[7] = size;
    $[8] = strokeWidth;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}